import { Plus } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form/form";
import { Button } from "@/components/ui/button/button";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createLeadForEmployeeAction } from "@/config/mutation/employee/lead";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { AxiosError } from "axios";

const formSchema = z.object({
  broker: z.string().optional(),
  name: z.string().optional(),
  clientId: z.string().optional(),
  leadId: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const CreateLead = () => {
  const key = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const [open, onOpenChange] = useState(false);

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      broker: "",
      name: "",
      clientId: "",
      leadId: "",
      email: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createLeadForEmployeeAction(key),
  });

  function onSubmit(values: FormValues) {
    if (!values.broker) {
      return toast.error("Please choose broker");
    }

    mutate(
      {
        name: values.name as string,
        email: values.email as string,
        phone: values.phone as string,
        broker: values.broker,
        allotToSelf: true,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leads"],
          });

          toast.success("Lead Created");
          form.reset();
          onOpenChange(false);
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING LEAD", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <>
      <Button
        size="sm"
        className="flex items-center gap-1"
        onClick={() => onOpenChange(true)}
      >
        <Plus className="w-4 h-4" />
        <span>Create Lead</span>
      </Button>

      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-3xl">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <AlertDialogHeader>
                <AlertDialogTitle>Create New Lead</AlertDialogTitle>
              </AlertDialogHeader>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-y-auto max-h-[500px] p-2">
                <FormField
                  control={form.control}
                  name="broker"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Broker</FormLabel>
                      <Select
                        disabled={isPending}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a broker" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[
                            "ANGELONE",
                            "ZERODHA",
                            "ALICEBLUE",
                            "ICICI",
                            "UPSTOX",
                          ].map((brkr) => {
                            return <SelectItem value={brkr}>{brkr}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} disabled={isPending} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} disabled={isPending} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client Id</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} disabled={isPending} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="leadId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lead Id</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} disabled={isPending} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} disabled={isPending} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <AlertDialogFooter className="mt-5">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button type="submit" disabled={isPending}>
                  Create
                </Button>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
